import { useTranslation as usePublicI18n } from "react-i18next";
import { publicText, publicTemplate } from "./publicText";
import React, { useEffect } from "react";
import { ExperimentOutlined, FieldTimeOutlined, FireOutlined, LineChartOutlined, ThunderboltOutlined } from "@ant-design/icons";
import PublicLayout from "./PublicLayout";
import { CASE01 } from "./caseStudyData";
import { formatNumber, Reveal, useDocumentTitle } from "./components/PageUtilities";
import { CaseNavigation, CaseStudyBreadcrumb, DataTable, Figure, KeyFinding, MethodGrid, MetricCard, ScientificNote, SectionHeader, StudyTimeline, TwoFigureGrid } from "./components/ScientificComponents";
import { OpenClosedBars } from "./components/ScientificCharts";
import { trackEvent } from "../FirebaseConfig";
function CaseStudy01() {
  usePublicI18n();
  useDocumentTitle(publicText("Case Study 01｜気孔状態と、その後の水分応答"));
  const tableRows = CASE01.results.map(item => ({
    ...item,
    condition: `${item.position}｜${item.label}`
  }));
  return <PublicLayout>
      <article className="science-case-page case-theme-01">
        <header className="science-case-hero">
          <div className="science-case-hero__grid" />
          <div className="science-orb orb-a" />
          <div className="science-orb orb-c" />

          <div className="case-container">
            <CaseStudyBreadcrumb current="01" />

            <div className="science-case-hero__layout">
              <Reveal className="science-case-hero__copy">
                <div className="science-case-id">{publicText("CASE STUDY 01")}</div>
                <span className="science-case-domain">{publicText("TREATMENT \xD7 WATER RESPONSE")}</span>
                <h1>{publicText("\u6C17\u5B54\u72B6\u614B\u3068\u3001")}<br />
                  <em>{publicText("\u305D\u306E\u5F8C\u306E\u6C34\u5206\u5FDC\u7B54")}</em>
                </h1>
                <p>{publicText(CASE01.lead)}</p>

                <div className="science-case-tags">
                  <span>{publicText("Tomato")}</span>
                  <span>{publicText("ABA")}</span>
                  <span>{publicText("Potassium")}</span>
                  <span>{publicText("Water control")}</span>
                  <span>{publicText("Rewatering")}</span>
                </div>

                <div className="science-case-hero__headline-result">
                  <div>
                    <small>{publicText("STATISTICAL TEST")}</small>
                    <strong>{publicText(CASE01.test.name)}</strong>
                  </div>
                  <div>
                    <small>{publicText("P VALUE")}</small>
                    <strong>{publicText("p ")}{publicText(CASE01.test.pValue)}</strong>
                  </div>
                </div>
              </Reveal>

              <Reveal className="science-case-hero__media case01-collage" delay={120}>
                <div className="case01-collage__main">
                  <img src={CASE01.images.treatment} alt={publicText("左からABA、カリウム、水処理のトマト苗")} />
                  <div className="case01-collage__labels">
                    <span>{publicText("ABA")}</span>
                    <span>{publicText("\u30AB\u30EA\u30A6\u30E0")}</span>
                    <span>{publicText("\u6C34")}</span>
                  </div>
                </div>
                <div className="case01-collage__thermal">
                  <img src={CASE01.images.thermal} alt={publicText("処理区を撮影したサーモグラフィー画像")} />
                  <span><FireOutlined />{publicText(" Thermal observation")}</span>
                </div>
                <div className="science-media-badge">
                  <ExperimentOutlined />
                  <div>
                    <strong>{publicText("414")}</strong>
                    <span>{publicText("detected stomata")}</span>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </header>

        <section className="science-section science-question-section">
          <div className="study-content">
            <SectionHeader index="01" eyebrow="STUDY QUESTION" title={publicText("処理直後の気孔状態は、3日後の外観応答とどう並ぶのか。")} description={publicText(CASE01.question)} />

            <KeyFinding title={publicText("同じ時点で、ABA区はほぼClosed、カリウム区はほぼOpen。")}>
              <p>{publicText("Open\u7387\u306FABA\u533A5.5%\u3001\u30AB\u30EA\u30A6\u30E0\u533A98.2%\u3001\u6C34\u533A79.8%\u3002 \u6C17\u5B54\u958B\u5EA6\u4E2D\u592E\u5024\u306F\u305D\u308C\u305E\u308C0.00\u30015.03\u30013.37 \xB5m\u3067\u3057\u305F\u3002 \u51E6\u7406\u533A\u306E\u9055\u3044\u306F\u3001Open\u7387\u3068\u958B\u5EA6\u5206\u5E03\u306E\u4E21\u65B9\u306B\u73FE\u308C\u307E\u3057\u305F\u3002")}</p>
            </KeyFinding>
          </div>
        </section>

        <section className="science-section is-soft">
          <div className="case-container">
            <SectionHeader index="02" eyebrow="EXPERIMENTAL FLOW" title={publicText("処理、気孔解析、無灌水、再灌水を時間軸で追跡。")} description={publicText("単発の数値比較ではなく、処理後の気孔状態と、その後の植物体の変化を同じCaseとして整理しました。")} />
            <StudyTimeline items={CASE01.timeline} />
          </div>
        </section>

        <section className="science-section">
          <div className="case-container">
            <SectionHeader index="03" eyebrow="INITIAL OBSERVATION" title={publicText("外観とサーモの次に、葉面の気孔を直接測る。")} description={publicText("サーモ画像は温度スケールが記録された定量図ではないため、本ページでは温度分布の観察画像として扱います。")} />

            <TwoFigureGrid>
              <Figure number="01A" title={publicText("処理区の外観")} src={CASE01.images.treatment} alt={publicText("左からABA、カリウム、水処理のトマト苗")} labels={["ABA", "カリウム", "水"]} caption={publicText("左からABA、カリウム、水（Control）。処理区の配置は以降の写真でも同じです。")} />
              <Figure number="01B" title={publicText("サーモグラフィー観察")} src={CASE01.images.thermal} alt={publicText("ABA、カリウム、水処理区のサーモ画像")} labels={["ABA", "カリウム", "水"]} caption={publicText("温度スケールが写っていないため、区間の絶対温度差は記載せず、表面温度分布の参考観察として掲載しています。")} />
            </TwoFigureGrid>
          </div>
        </section>

        <section className="science-section science-results-section">
          <div className="case-container">
            <SectionHeader index="04" eyebrow="STOMATAL RESPONSE" title={publicText("Open率と気孔開度で、3処理の応答が明瞭に分かれた。")} description={publicText("Closedと判定された気孔は0 µmとして全気孔解析に含めています。")} />

            <Reveal className="science-metric-grid is-three">
              {publicText(CASE01.results.map((item, index) => <MetricCard key={item.key} label={publicText(`${item.position}｜${item.label}`)} value={item.openRate.toFixed(1)} unit="% Open" caption={publicTemplate(48, [item.median.toFixed(2), item.detected])} tone={["red", "green", "blue"][index]} icon={<ThunderboltOutlined />} />))}
            </Reveal>

            <Reveal className="science-result-panel">
              <div className="science-result-panel__header">
                <div>
                  <span>{publicText("OPEN / CLOSED COMPOSITION")}</span>
                  <h3>{publicText("\u6C17\u5B54\u96C6\u56E3\u306E\u69CB\u6210")}</h3>
                </div>
                <small>{publicText("\u500B\u3005\u306E\u691C\u51FA\u6C17\u5B54\u3092\u96C6\u8A08")}</small>
              </div>
              <OpenClosedBars items={CASE01.results} />
            </Reveal>

            <Figure number="02" title={publicText("StomataScope Analysisによる3群比較")} src={CASE01.images.analysis} alt={publicText("ABA、カリウム、水処理区の気孔開度比較画面")} contain caption={publicText("箱ひげ図、個々の気孔、ヒストグラム、集計詳細を同一画面で確認。Kruskal–Wallis検定はp<0.0001でした。")} className="science-figure--analysis" />

            <DataTable caption={publicText("Table 1. 処理区別の気孔解析結果")} columns={[{
            key: "condition",
            label: "処理区"
          }, {
            key: "detected",
            label: "検出気孔",
            align: "right"
          }, {
            key: "openRate",
            label: "Open率",
            unit: "%",
            align: "right",
            render: value => value.toFixed(1)
          }, {
            key: "median",
            label: "中央値",
            unit: "µm",
            align: "right",
            render: value => value.toFixed(2)
          }, {
            key: "mean",
            label: "平均",
            unit: "µm",
            align: "right",
            render: value => value.toFixed(2)
          }, {
            key: "sd",
            label: "SD",
            unit: "µm",
            align: "right",
            render: value => value.toFixed(2)
          }, {
            key: "q1",
            label: "Q1",
            unit: "µm",
            align: "right",
            render: value => value.toFixed(2)
          }, {
            key: "q3",
            label: "Q3",
            unit: "µm",
            align: "right",
            render: value => value.toFixed(2)
          }]} rows={tableRows} />

            <ScientificNote title={publicText(`${CASE01.test.name}: p ${CASE01.test.pValue}`)} tone="success">
              <p>{publicText(CASE01.test.interpretation)}</p>
            </ScientificNote>
          </div>
        </section>

        <section className="science-section is-dark">
          <div className="case-container">
            <SectionHeader index="05" eyebrow="DAY 3 / WATER RESPONSE" title={publicText("無灌水後の萎れと、再灌水後の回復を観察。")} description={publicText("処理後は灌水を停止し、3日後12:05に撮影。その後に灌水を行い、17:07に再撮影しました。")} />

            <div className="science-before-after">
              <Figure number="03A" title={publicText("Day 3 / 12:05 / 再灌水前")} src={CASE01.images.dry} alt={publicText("3日間無灌水後のABA、カリウム、水処理苗")} labels={["ABA", "カリウム", "水"]} caption={publicText("ABA区は外観を比較的維持し、カリウム区と水区では強い萎れが観察されました。代表個体の外観観察です。")} />
              <div className="science-before-after__arrow">
                <FieldTimeOutlined />
                <span>{publicText("REWATER")}</span>
                <strong>{publicText("\u7D045\u6642\u9593\u5F8C")}</strong>
              </div>
              <Figure number="03B" title={publicText("Day 3 / 17:07 / 再灌水後")} src={CASE01.images.rewater} alt={publicText("再灌水後のABA、カリウム、水処理苗")} labels={["ABA", "カリウム", "水"]} caption={publicText("カリウム区と水区で外観上の回復が確認されました。回復量は画像からの定量値ではありません。")} />
            </div>
          </div>
        </section>

        <section className="science-section">
          <div className="study-content">
            <SectionHeader index="06" eyebrow="INTERPRETATION" title={publicText("気孔は“その後”を考えるための情報になる。ただし、単独で因果は決めない。")} />

            <Reveal className="science-prose-columns">
              <div>
                <h3>{publicText("\u3053\u306E\u4E8B\u4F8B\u304B\u3089\u89B3\u5BDF\u3067\u304D\u305F\u3053\u3068")}</h3>
                <ul>
                  <li>{publicText("\u51E6\u7406\u76F4\u5F8C\u306EOpen\u7387\u3068\u6C17\u5B54\u958B\u5EA6\u306F3\u533A\u3067\u5927\u304D\u304F\u7570\u306A\u3063\u305F\u3002")}</li>
                  <li>{publicText("\u7121\u704C\u6C343\u65E5\u5F8C\u306E\u5916\u89B3\u5FDC\u7B54\u3082\u51E6\u7406\u533A\u3067\u7570\u306A\u3063\u305F\u3002")}</li>
                  <li>{publicText("\u518D\u704C\u6C34\u5F8C\u3001\u840E\u308C\u3066\u3044\u305F\u533A\u3067\u5916\u89B3\u4E0A\u306E\u56DE\u5FA9\u304C\u78BA\u8A8D\u3055\u308C\u305F\u3002")}</li>
                  <li>{publicText("\u6C17\u5B54\u72B6\u614B\u3068\u5F8C\u306E\u6C34\u5206\u5FDC\u7B54\u3092\u3001\u540C\u3058\u6642\u9593\u8EF8\u3067\u6BD4\u8F03\u3067\u304D\u305F\u3002")}</li>
                </ul>
              </div>
              <div>
                <h3>{publicText("\u3053\u306E\u4E8B\u4F8B\u3060\u3051\u3067\u306F\u8A3C\u660E\u3067\u304D\u306A\u3044\u3053\u3068")}</h3>
                <ul>
                  <li>{publicText("ABA\u307E\u305F\u306F\u30AB\u30EA\u30A6\u30E0\u306E\u4E00\u822C\u7684\u306A\u4E7E\u71E5\u8010\u6027\u52B9\u679C\u3002")}</li>
                  <li>{publicText("\u51E6\u7406\u9593\u306E\u512A\u52A3\u3001\u53CE\u91CF\u30FB\u54C1\u8CEA\u3078\u306E\u52B9\u679C\u3002")}</li>
                  <li>{publicText("\u6C17\u5B54\u72B6\u614B\u304C3\u65E5\u5F8C\u306E\u840E\u308C\u3092\u76F4\u63A5\u5F15\u304D\u8D77\u3053\u3057\u305F\u3068\u3044\u3046\u56E0\u679C\u3002")}</li>
                  <li>{publicText("\u5199\u771F\u306B\u5199\u308B\u4EE3\u8868\u500B\u4F53\u304B\u3089\u6BCD\u96C6\u56E3\u3078\u4E00\u822C\u5316\u3057\u305F\u7D50\u8AD6\u3002")}</li>
                </ul>
              </div>
            </Reveal>

            <MethodGrid items={[{
            label: "解析単位",
            value: "検出気孔",
            note: "Closedは0 µmとして含む"
          }, {
            label: "検出総数",
            value: formatNumber(414, 0),
            note: "139 + 167 + 108"
          }, {
            label: "群間検定",
            value: "Kruskal–Wallis",
            note: "p < 0.0001"
          }, {
            label: "外観追跡",
            value: "Day 0 → Day 3",
            note: "無灌水・再灌水"
          }]} />

            <ScientificNote title={publicText("研究設計上の注意")} tone="caution">
              <p>{publicText("\u6C17\u5B54\u6570\u306F\u591A\u6570\u3067\u3082\u3001\u540C\u4E00\u8449\u30FB\u540C\u4E00\u500B\u4F53\u5185\u306E\u6C17\u5B54\u306F\u5B8C\u5168\u306B\u72EC\u7ACB\u3067\u306F\u3042\u308A\u307E\u305B\u3093\u3002 \u51E6\u7406\u52B9\u679C\u3092\u691C\u8A3C\u3059\u308B\u306B\u306F\u3001\u8907\u6570\u306E\u72EC\u7ACB\u500B\u4F53\u3092\u751F\u7269\u5B66\u7684\u53CD\u5FA9\u3068\u3057\u3066\u8A2D\u8A08\u3057\u3001 \u8449\u30FB\u500B\u4F53\u30FB\u51E6\u7406\u3092\u968E\u5C64\u5316\u3057\u305F\u89E3\u6790\u304C\u5FC5\u8981\u3067\u3059\u3002")}</p>
            </ScientificNote>
          </div>
        </section>

        <section className="science-case-conclusion">
          <div className="study-content">
            <Reveal>
              <LineChartOutlined />
              <span>{publicText("FROM STOMATA TO RESPONSE")}</span>
              <h2>{publicText("\u51E6\u7406\u6642\u70B9\u306E\u6C17\u5B54\u3092\u6E2C\u308A\u3001\u305D\u306E\u5F8C\u306E\u690D\u7269\u3092\u8FFD\u3046\u3002")}</h2>
              <p>{publicText("StomataScope\u306F\u3001\u51E6\u7406\u76F4\u5F8C\u306E\u5FAE\u5C0F\u306A\u751F\u7406\u5FDC\u7B54\u3068\u3001\u6570\u6642\u9593\u30FB\u6570\u65E5\u5F8C\u306E\u5916\u89B3\u5909\u5316\u3092\u3064\u306A\u3052\u3066\u691C\u8A0E\u3059\u308B\u305F\u3081\u306E\u89B3\u6E2C\u5C64\u3092\u63D0\u4F9B\u3057\u307E\u3059\u3002")}</p>
            </Reveal>
          </div>
        </section>

        <div className="case-container">
          <CaseNavigation next={{
          id: "02",
          path: "/case-studies/02",
          title: "萎れと気孔閉鎖は同じなのか？"
        }} />
        </div>
      </article>
    </PublicLayout>;
}
export default CaseStudy01;
